import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Skeleton,
  MenuItem,
  Select,
  FormControl,
  Chip,
  Checkbox,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  Autocomplete,
  OverlayView,
} from "@react-google-maps/api";
import config from "../../config";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteModal from "../common/DeleteModal";
import axios from "axios";
import { getDestinationStopIcon, getOriginStopIcon } from "../school/icons";
import { getPlaceConfig } from "../dashboard/functions.js";
import useConfiguredGoogleMap from "../../hooks/useConfiguredGoogleMap.js";
import { useTranslation } from "react-i18next";

const libraries = ["places", "geometry"];

// ─── Styled Components (unchanged) ───────────────────────────────────────────

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#E8EAEE",
  borderRadius: "10px",
  borderBottom: "1px solid #C8CBD0",
  padding: "24px",
  marginBottom: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  position: "relative",
}));

const CreateButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F9E719",
  color: "#1F242E",
  height: "39px",
  fontFamily: "Roboto",
  fontWeight: 600,
  borderRadius: "8px",
  border: "1px solid #414958",
  padding: "10px 16px",
  textTransform: "none",
  fontSize: "14px",
  "&:hover": { backgroundColor: "#F9E719" },
}));

const StyledTextField = styled(TextField)(({ theme, error }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    height: "55px",
    "& fieldset": {
      borderColor: error ? "#d32f2f" : "#183059",
      borderWidth: "2px",
    },
    "&:hover fieldset": { borderColor: error ? "#d32f2f" : "#183059" },
    "&.Mui-focused fieldset": { borderColor: error ? "#d32f2f" : "#183059" },
  },
  "& .MuiOutlinedInput-input": { padding: "16px 24px" },
}));

const StyledSelect = styled(Select)(({ theme, error }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  minHeight: "55px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? "#d32f2f" : "#183059",
    borderWidth: "2px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? "#d32f2f" : "#183059",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: error ? "#d32f2f" : "#183059",
  },
  "& .MuiSelect-select": { padding: "16px 24px" },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
  marginLeft: "14px",
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F9E719",
  color: "#1F242E",
  height: "55px",
  border: "1px solid #414958",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  textTransform: "none",
  fontSize: "20px",
  "&:hover": { backgroundColor: "#F9E719" },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#F2F2F3",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontWeight: "bold",
  border: "1px solid #414958",
  color: "#1F242E",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": { backgroundColor: "#F2F2F3" },
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  color: "#cc0000",
  height: "55px",
  borderRadius: "8px",
  padding: "16px 24px",
  fontSize: "20px",
  textTransform: "none",
  "&:hover": { backgroundColor: "rgba(204, 0, 0, 0.05)" },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "#414958",
  marginBottom: "16px",
  backgroundColor: "#B7BFCC",
  borderRadius: "8px",
  padding: "8px",
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: "20px",
}));

const SearchBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 10,
  width: "90%",
  maxWidth: "400px",
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": { borderColor: "#C8CBD0" },
    "&:hover fieldset": { borderColor: "#183059" },
    "&.Mui-focused fieldset": { borderColor: "#183059" },
  },
}));

const RequiredAsterisk = () => (
  <Box component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>
    *
  </Box>
);

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "16px",
};
const defaultCenter = { lat: 12.9716, lng: 77.5946 };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getCoordinateKey = (lat, lng) =>
  `${Number(lat).toFixed(6)}_${Number(lng).toFixed(6)}`;

const buildFallbackStop = (point, label) => {
  if (!point?.latitude || !point?.longitude) return null;
  return {
    id:
      point.id ||
      `fallback-${label.toLowerCase()}-${getCoordinateKey(point.latitude, point.longitude)}`,
    name: point.name || label,
    latitude: Number(point.latitude),
    longitude: Number(point.longitude),
    isSynthetic: true,
  };
};

const mergeStopsByIdAndCoordinate = (baseStops = [], extraStops = []) => {
  const merged = [];
  const seenIds = new Set();
  const seenCoordinates = new Set();
  [...baseStops, ...extraStops].forEach((stop) => {
    if (!stop) return;
    const stopId = stop.id;
    const coordinateKey = getCoordinateKey(stop.latitude, stop.longitude);
    if (stopId && seenIds.has(stopId)) return;
    if (seenCoordinates.has(coordinateKey)) return;
    if (stopId) seenIds.add(stopId);
    seenCoordinates.add(coordinateKey);
    merged.push(stop);
  });
  return merged;
};

const buildRoutePointFromStop = (stop) => {
  if (!stop) return null;
  return {
    lat: Number(stop.latitude),
    lng: Number(stop.longitude),
    isStop: true,
    stopId: stop.id,
    name: stop.name,
    id: stop.id,
  };
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function CreateRouteForm({
  onClose,
  onSubmit,
  initialData = null,
  isEdit = false,
  onDelete,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Build fallback stop options from initialData so the form has something
  // to show before real stops load. These may have synthetic IDs.
  const fallbackOriginStop = buildFallbackStop(initialData?.origin, "Origin");
  const fallbackDestinationStop = buildFallbackStop(
    initialData?.destination,
    "Destination",
  );
  const fallbackRouteStops = Array.isArray(initialData?.routePoints)
    ? initialData.routePoints
        .filter((p) => p?.isStop || p?.stopId)
        .map((p, i) =>
          buildFallbackStop(
            {
              id: p.stopId || p.id,
              latitude: p.lat,
              longitude: p.lng,
              name: p.name || `Stop ${i + 1}`,
            },
            `Stop ${i + 1}`,
          ),
        )
    : [];

  const initialStopOptions = mergeStopsByIdAndCoordinate(
    Array.isArray(initialData?.stops) ? initialData.stops : [],
    [fallbackOriginStop, fallbackDestinationStop, ...fallbackRouteStops],
  );

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stopsData, setStopsData] = useState([]);
  const [vehicleData, setVehicleData] = useState([]);
  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [intermediatePointCount, setIntermediatePointCount] = useState(0);
  const { placeMarkers, getMapOptions } = useConfiguredGoogleMap({
    map,
    isLoaded,
  });

  const [deleteError, setDeleteError] = useState("");

  // ── formData initialisation ──────────────────────────────────────────────
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      const stopsArr = initialStopOptions;
      const stopsById = new Map(
        stopsArr.filter((s) => s?.id).map((s) => [s.id, s]),
      );
      const stopsByCoordinate = new Map(
        stopsArr.map((s) => [getCoordinateKey(s.latitude, s.longitude), s]),
      );

      const routePointsArr = Array.isArray(initialData.routePoints)
        ? initialData.routePoints.map((point, index) => {
            const fallback =
              point.isStop || point.stopId
                ? buildFallbackStop(
                    {
                      id: point.stopId || point.id,
                      latitude: point.lat,
                      longitude: point.lng,
                      name: point.name || `Stop ${index + 1}`,
                    },
                    `Stop ${index + 1}`,
                  )
                : null;
            const matched =
              (point.stopId && stopsById.get(point.stopId)) ||
              stopsByCoordinate.get(getCoordinateKey(point.lat, point.lng)) ||
              fallback;

            if (matched || point.isStop || point.stopId) {
              return {
                ...point,
                lat: Number(point.lat),
                lng: Number(point.lng),
                isStop: true,
                stopId: matched?.id || point.stopId || null,
                name: matched?.name || point.name || `Stop ${index + 1}`,
                id:
                  matched?.id ||
                  point.stopId ||
                  point.id ||
                  `route-stop-${index}`,
              };
            }
            return {
              ...point,
              lat: Number(point.lat),
              lng: Number(point.lng),
              isStop: false,
              stopId: null,
              name: point.name || `IM${index + 1}`,
              id: point.id || `intermediate-${index}-${Number(point.lat)}`,
            };
          })
        : [];

      const originStop =
        (initialData.origin?.id && stopsById.get(initialData.origin.id)) ||
        stopsByCoordinate.get(
          getCoordinateKey(
            initialData.origin?.latitude,
            initialData.origin?.longitude,
          ),
        ) ||
        fallbackOriginStop;

      const destinationStop =
        (initialData.destination?.id &&
          stopsById.get(initialData.destination.id)) ||
        stopsByCoordinate.get(
          getCoordinateKey(
            initialData.destination?.latitude,
            initialData.destination?.longitude,
          ),
        ) ||
        fallbackDestinationStop ||
        originStop;

      const stopIdsFromRoutePoints = routePointsArr
        .filter(
          (p) =>
            p.isStop &&
            p.stopId &&
            p.stopId !== originStop?.id &&
            p.stopId !== destinationStop?.id,
        )
        .map((p) => p.stopId);

      return {
        routeId: initialData.routeId || "",
        routeName: initialData.name || "",
        type: initialData.routeType || "UNI_DIRCTIONAL",
        color: initialData.routeColor || "#0066FF",
        pickUp: originStop?.id || "",
        drop: destinationStop?.id || "",
        stops: stopIdsFromRoutePoints,
        vehicles: Array.isArray(initialData.vehicles)
          ? initialData.vehicles.map((v) => v.id)
          : [],
        routePoints: routePointsArr,
        intermediatePoints: routePointsArr.filter((p) => !p.isStop),
      };
    }

    return {
      routeId: "",
      routeName: "",
      type: "UNI_DIRCTIONAL",
      color: "#0066FF",
      pickUp: "",
      drop: "",
      stops: [],
      vehicles: [],
      routePoints: [],
      intermediatePoints: [],
    };
  });

  const [errors, setErrors] = useState({
    routeId: "",
    routeName: "",
    pickUp: "",
    drop: "",
  });
  const [touched, setTouched] = useState({
    routeId: false,
    routeName: false,
    pickUp: false,
    drop: false,
  });

  // ── Fetch stops & vehicles ────────────────────────────────────────────────
  useEffect(() => {
    fetchStops();
    fetchVehicles();
  }, []);

  // ── KEY FIX: Reconcile formData IDs after real stops load ─────────────────
  //
  // Problem: on initial render (especially in edit mode), `formData.pickUp`,
  // `formData.drop`, and stop IDs inside `formData.routePoints` may be
  // synthetic fallback IDs (e.g. "fallback-origin-12.34_77.56").
  // Once real stops arrive from the API they have real UUIDs, so
  // `stopsData.find(s => s.id === formData.pickUp)` returns undefined,
  // breaking markers and route-plotting.
  //
  // Solution: after `stopsData` is populated, walk every stored ID and
  // re-map it to the matching real stop (looked up by coordinate).
  useEffect(() => {
    if (!isEdit || !initialData || stopsData.length === 0) return;

    const findByCoord = (lat, lng) => {
      if (lat == null || lng == null) return undefined;
      const key = getCoordinateKey(lat, lng);
      return stopsData.find(
        (s) => getCoordinateKey(s.latitude, s.longitude) === key,
      );
    };

    const findById = (id) => stopsData.find((s) => s.id === id);

    setFormData((prev) => {
      let changed = false;
      let newPickUp = prev.pickUp;
      let newDrop = prev.drop;

      // --- Reconcile pickUp ---
      if (!findById(prev.pickUp) && initialData.origin) {
        const real =
          findById(initialData.origin.id) ||
          findByCoord(
            initialData.origin.latitude,
            initialData.origin.longitude,
          );
        if (real && real.id !== prev.pickUp) {
          newPickUp = real.id;
          changed = true;
        }
      }

      // --- Reconcile drop ---
      if (!findById(prev.drop) && initialData.destination) {
        const real =
          findById(initialData.destination.id) ||
          findByCoord(
            initialData.destination.latitude,
            initialData.destination.longitude,
          );
        if (real && real.id !== prev.drop) {
          newDrop = real.id;
          changed = true;
        }
      }

      // --- Reconcile routePoints ---
      const newRoutePoints = prev.routePoints.map((point) => {
        if (!point.isStop) return point; // intermediate – no ID needed

        // Already a valid real ID?
        if (findById(point.stopId)) return point;

        // Try to find real stop by coordinate
        const real = findByCoord(point.lat, point.lng);
        if (real && real.id !== point.stopId) {
          changed = true;
          return { ...point, stopId: real.id, name: real.name, id: real.id };
        }
        return point;
      });

      if (!changed) return prev; // no-op if nothing changed

      // Rebuild `stops` (intermediate stop IDs excluding origin & destination)
      const newStops = newRoutePoints
        .filter(
          (p) =>
            p.isStop &&
            p.stopId &&
            p.stopId !== newPickUp &&
            p.stopId !== newDrop,
        )
        .map((p) => p.stopId);

      return {
        ...prev,
        pickUp: newPickUp,
        drop: newDrop,
        routePoints: newRoutePoints,
        stops: newStops,
      };
    });
  }, [stopsData, isEdit, initialData]); // run whenever stopsData changes

  // ── Intermediate points counter ───────────────────────────────────────────
  useEffect(() => {
    const intermediatePoints = formData.routePoints.filter((p) => !p.isStop);
    setIntermediatePointCount(intermediatePoints.length);
    setFormData((prev) => {
      const hasSame =
        prev.intermediatePoints.length === intermediatePoints.length &&
        prev.intermediatePoints.every(
          (p, i) => p.id === intermediatePoints[i]?.id,
        );
      if (hasSame) return prev;
      return { ...prev, intermediatePoints };
    });
  }, [formData.routePoints]);

  // ── Route plotting ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoaded && map && formData.pickUp && formData.drop) {
      plotRoute();
    }
  }, [
    isLoaded,
    map,
    formData.pickUp,
    formData.drop,
    formData.stops,
    formData.color,
    stopsData.length,
    formData.routePoints,
  ]);

  useEffect(() => {
    if (isLoaded && map && initialData && stopsData.length > 0) {
      const timer = setTimeout(() => plotRoute(), 500);
      return () => clearTimeout(timer);
    }
  }, [isLoaded, map, stopsData.length, initialData]);

  // ── API helpers ───────────────────────────────────────────────────────────
  const fetchStops = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/stop/organization`, {
        withCredentials: true,
      });
      const fetched = Array.isArray(response.data) ? response.data : [];
      // Merge real stops with fallbacks; real stops take precedence by coordinate
      setStopsData(
        mergeStopsByIdAndCoordinate(fetched, [
          fallbackOriginStop,
          fallbackDestinationStop,
          ...fallbackRouteStops,
        ]),
      );
      console.log("=== STOPS LOADED ===");
      console.log("stopsData count:", fetched.length);
      console.log(
        "Does stopsData contain pickUp?",
        fetched.find((s) => s.id === formData.pickUp),
      );
      console.log(
        "Does stopsData contain drop?",
        fetched.find((s) => s.id === formData.drop),
      );
    } catch (error) {
      console.error("Error fetching stops:", error);
      setStopsData(
        mergeStopsByIdAndCoordinate(
          [],
          [fallbackOriginStop, fallbackDestinationStop, ...fallbackRouteStops],
        ),
      );
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(
        `${config.baseURL}/vehicle/organization`,
        {
          withCredentials: true,
        },
      );
      setVehicleData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateField = (field, value) => {
    switch (field) {
      case "routeId":
        if (!value || value.trim() === "") return t("Route ID is required");
        if (!/^[a-zA-Z0-9\s-_]+$/.test(value.trim()))
          return t(
            "Route ID can contain only letters, numbers, spaces, hyphen, and underscore",
          );
        return "";
      case "routeName":
        if (!value || value.trim() === "") return t("Route Name is required");
        if (!/^[a-zA-Z0-9\s]+$/.test(value.trim()))
          return t("Route Name can contain only letters, numbers, and spaces");
        return "";
      case "pickUp":
        if (!value || value === "") return t("Pick-up location is required");
        return "";
      case "drop":
        if (formData.type !== "BI_DIRECTIONAL") {
          if (!value || value === "") return t("Drop location is required");
          if (value === formData.pickUp) return t("Drop location cannot be same as Pick-up");
        }
        return "";
      default:
        return "";
    }
  };

  // ── Map ───────────────────────────────────────────────────────────────────
  const plotRoute = () => {
    if (!window.google || !map) return;
    const pickupStop = stopsData.find((s) => s.id === formData.pickUp);
    const dropStop = stopsData.find((s) => s.id === formData.drop);
    if (!pickupStop || !dropStop) return;

    const directionsServiceInstance =
      new window.google.maps.DirectionsService();
    const waypoints = formData.routePoints.map((point) => ({
      location: { lat: point.lat, lng: point.lng },
      stopover: Boolean(point.isStop),
    }));

    const request = {
      origin: {
        lat: Number(pickupStop.latitude),
        lng: Number(pickupStop.longitude),
      },
      destination: {
        lat: Number(dropStop.latitude),
        lng: Number(dropStop.longitude),
      },
      waypoints,
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    };

    directionsServiceInstance.route(request, (result, status) => {
      if (
        status === window.google.maps.DirectionsStatus.OK ||
        status === "OK"
      ) {
        setDirections(result);
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({
          lat: Number(pickupStop.latitude),
          lng: Number(pickupStop.longitude),
        });
        bounds.extend({
          lat: Number(dropStop.latitude),
          lng: Number(dropStop.longitude),
        });
        formData.routePoints.forEach((p) =>
          bounds.extend({ lat: p.lat, lng: p.lng }),
        );
        map.fitBounds(bounds);
      } else {
        setDirections(null);
      }
    });
  };

  const handleMapClick = useCallback(
    (e) => {
      if (!window.google || !map) return;
      const pickupStop = stopsData.find((s) => s.id === formData.pickUp);
      const dropStop = stopsData.find((s) => s.id === formData.drop);
      if (!pickupStop || !dropStop) return;

      const clickedLat = e.latLng.lat();
      const clickedLng = e.latLng.lng();

      const allPoints = [
        { lat: Number(pickupStop.latitude), lng: Number(pickupStop.longitude) },
        ...formData.routePoints,
        { lat: Number(dropStop.latitude), lng: Number(dropStop.longitude) },
      ];

      let insertIndex = 0;
      let minDistance = Infinity;
      for (let i = 0; i < allPoints.length - 1; i++) {
        const start = new window.google.maps.LatLng(
          allPoints[i].lat,
          allPoints[i].lng,
        );
        const end = new window.google.maps.LatLng(
          allPoints[i + 1].lat,
          allPoints[i + 1].lng,
        );
        const clicked = new window.google.maps.LatLng(clickedLat, clickedLng);
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            clicked,
            new window.google.maps.LatLng(
              (start.lat() + end.lat()) / 2,
              (start.lng() + end.lng()) / 2,
            ),
          );
        if (distance < minDistance) {
          minDistance = distance;
          insertIndex = i;
        }
      }

      const newPoint = {
        lat: clickedLat,
        lng: clickedLng,
        isStop: false,
        stopId: null,
        name: `IM${formData.routePoints.length + 1}`,
        id: `intermediate-${Date.now()}-${formData.routePoints.length + 1}`,
      };

      const updated = [...formData.routePoints];
      if (insertIndex === 0) updated.unshift(newPoint);
      else if (insertIndex >= formData.routePoints.length)
        updated.push(newPoint);
      else updated.splice(insertIndex, 0, newPoint);

      setFormData((prev) => ({ ...prev, routePoints: updated }));
    },
    [formData, stopsData, map],
  );

  const onLoad = useCallback((mapInstance) => setMap(mapInstance), []);
  const onUnmount = useCallback(() => setMap(null), []);
  const onLoadAutocomplete = (ac) => setAutocomplete(ac);
  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        map.panTo(place.geometry.location);
        map.setZoom(15);
      }
    }
  };

  // ── Form handlers ─────────────────────────────────────────────────────────
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({ ...formData, [field]: value });
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));

    if (field === "pickUp") {
      const dropError =
        value === formData.drop
          ? t("Drop location cannot be same as Pick-up")
          : "";
      setErrors((prev) => ({ ...prev, drop: dropError }));
    }
    if (field === "drop") {
      const dropError =
        value === formData.pickUp
          ? t("Drop location cannot be same as Pick-up")
          : "";
      setErrors((prev) => ({ ...prev, drop: dropError }));
    }
    if (field === "type" && value === "BI_DIRECTIONAL" && formData.pickUp) {
      setFormData((prev) => ({ ...prev, drop: prev.pickUp }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleColorChange = (e) =>
    setFormData({ ...formData, color: e.target.value });

  const handleStopsChange = (event) => {
    const newStops = event.target.value;
    const filteredStopIds = newStops.filter(
      (id) => id !== formData.pickUp && id !== formData.drop,
    );
    const currentIntermediateStopIds = formData.routePoints
      .filter(
        (p) =>
          p.isStop &&
          p.stopId &&
          p.stopId !== formData.pickUp &&
          p.stopId !== formData.drop,
      )
      .map((p) => p.stopId);
    const preservedRoutePoints = formData.routePoints.filter(
      (p) => !p.isStop || filteredStopIds.includes(p.stopId),
    );
    const newStopPoints = filteredStopIds
      .filter((stopId) => !currentIntermediateStopIds.includes(stopId))
      .map((stopId) =>
        buildRoutePointFromStop(stopsData.find((s) => s.id === stopId)),
      )
      .filter(Boolean);

    setFormData({
      ...formData,
      stops: filteredStopIds,
      routePoints: [...preservedRoutePoints, ...newStopPoints],
    });
  };

  const handleVehiclesChange = (e) =>
    setFormData({ ...formData, vehicles: e.target.value });

  const handleRemoveStop = (stopToRemove) =>
    setFormData({
      ...formData,
      stops: formData.stops.filter((s) => s !== stopToRemove),
      routePoints: formData.routePoints.filter(
        (p) => !(p.isStop && p.stopId === stopToRemove),
      ),
    });

  const handleRemoveVehicle = (vehicleToRemove) =>
    setFormData({
      ...formData,
      vehicles: formData.vehicles.filter((v) => v !== vehicleToRemove),
    });

  const handleRemoveIntermediatePoint = (pointId) =>
    setFormData((prev) => ({
      ...prev,
      routePoints: prev.routePoints.filter((p) => p.id !== pointId),
      intermediatePoints: prev.intermediatePoints.filter(
        (p) => p.id !== pointId,
      ),
    }));

  const handleDeleteClick = () => setShowDeleteModal(true);
  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    if (onDelete) {
      try {
        await onDelete();
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          "Failed to delete route.";
        setDeleteError(message);
        setTimeout(() => setDeleteError(""), 5000);
      }
    }
  };
  const handleDeleteCancel = () => setShowDeleteModal(false);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const requiredFields = ["routeId", "routeName", "pickUp"];
    if (formData.type !== "BI_DIRECTIONAL") requiredFields.push("drop");

    const newTouched = { ...touched };
    const newErrors = { ...errors };
    requiredFields.forEach((field) => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    setTouched(newTouched);
    setErrors(newErrors);
    if (requiredFields.some((field) => newErrors[field])) return;

    const routeCoordinates = [];
    const pickupStop = stopsData.find((s) => s.id === formData.pickUp);
    if (pickupStop) {
      routeCoordinates.push({
        longitude: parseFloat(Number(pickupStop.longitude).toFixed(6)),
        latitude: parseFloat(Number(pickupStop.latitude).toFixed(6)),
        isStop: true,
        stopId: pickupStop.id,
      });
    }
    formData.routePoints.forEach((point) => {
      routeCoordinates.push({
        longitude: parseFloat(Number(point.lng).toFixed(6)),
        latitude: parseFloat(Number(point.lat).toFixed(6)),
        isStop: Boolean(point.isStop),
        stopId: point.isStop ? point.stopId : null,
      });
    });
    const dropStop = stopsData.find((s) => s.id === formData.drop);
    if (dropStop) {
      routeCoordinates.push({
        longitude: parseFloat(Number(dropStop.longitude).toFixed(6)),
        latitude: parseFloat(Number(dropStop.latitude).toFixed(6)),
        isStop: true,
        stopId: dropStop.id,
      });
    }

    const apiData = {
      routeId: formData.routeId,
      name: formData.routeName,
      routeType: formData.type,
      routeColor: formData.color,
      routeCoordinates,
    };
    if (isEdit && initialData) apiData.id = initialData.id;

    if (onSubmit) await onSubmit(apiData, formData.vehicles);

    if (!isEdit) {
      setFormData({
        routeId: "",
        routeName: "",
        type: "UNI_DIRCTIONAL",
        color: "#0066FF",
        pickUp: "",
        drop: "",
        stops: [],
        vehicles: [],
        routePoints: [],
        intermediatePoints: [],
      });
      setErrors({ routeId: "", routeName: "", pickUp: "", drop: "" });
      setTouched({
        routeId: false,
        routeName: false,
        pickUp: false,
        drop: false,
      });
      setIntermediatePointCount(0);
    }
  };

  // ── Icon helpers ──────────────────────────────────────────────────────────
  const getIntermediatePointIcon = () => {
    const svg = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="6" fill="#183059" stroke="#fff" stroke-width="2"/></svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(16, 16),
      anchor: new window.google.maps.Point(8, 8),
    };
  };

  const getIntermediateIcon = () => {
    const svg = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="16" height="16" rx="8" stroke="#008C23" stroke-width="4"/><circle cx="10" cy="10" r="6" fill="white"/></svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(20, 20),
      anchor: new window.google.maps.Point(10, 10),
    };
  };

  const getIntermediateStops = () =>
    formData.routePoints
      .filter(
        (p) =>
          p.isStop &&
          p.stopId &&
          p.stopId !== formData.pickUp &&
          p.stopId !== formData.drop,
      )
      .map((p) => p.stopId);

  const getIntermediateRoutePoints = () =>
    formData.routePoints.filter((p) => !p.isStop);

  console.log("=== INIT CHECK ===");
  console.log("initialData.origin:", initialData?.origin);
  console.log("initialData.destination:", initialData?.destination);
  console.log("formData.pickUp set to:", formData.pickUp);
  console.log("formData.drop set to:", formData.drop);

  console.log(deleteError, "here is the error");

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) {
    return (
      <FormContainer>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={28}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={56}
                    sx={{ borderRadius: "16px" }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={5}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={420}
              sx={{ borderRadius: "16px" }}
            />
          </Grid>
        </Grid>
      </FormContainer>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <FormContainer>
      <Grid container spacing={3}>
        {/* ── Left: Form Fields ── */}
        <Grid item xs={12} md={7}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {t("ID")} <RequiredAsterisk />
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.routeId}
                onChange={handleChange("routeId")}
                onBlur={handleBlur("routeId")}
                placeholder={t("Route ID")}
                error={touched.routeId && !!errors.routeId}
              />
              {touched.routeId && errors.routeId && (
                <ErrorText>{errors.routeId}</ErrorText>
              )}
            </Grid>

            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {t("Name")} <RequiredAsterisk />
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.routeName}
                onChange={handleChange("routeName")}
                onBlur={handleBlur("routeName")}
                placeholder={t("Route Name")}
                error={touched.routeName && !!errors.routeName}
              />
              {touched.routeName && errors.routeName && (
                <ErrorText>{errors.routeName}</ErrorText>
              )}
            </Grid>

            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                }}
              >
                {t("Color")}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: "55px",
                  backgroundColor: "#fff",
                  border: "2px solid #183059",
                  borderRadius: "16px",
                  cursor: "pointer",
                  color: "#C8CBD0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingX: "16px",
                }}
              >
                <span>{t("Click to select color")}</span>
                <input
                  type="color"
                  value={formData.color}
                  onChange={handleColorChange}
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    background: "transparent",
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {t("Pick-up")} <RequiredAsterisk />
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={formData.pickUp}
                  onChange={handleChange("pickUp")}
                  onBlur={handleBlur("pickUp")}
                  displayEmpty
                  error={touched.pickUp && !!errors.pickUp}
                >
                  <MenuItem value="" disabled>
                    {t("Select Pick-up")}
                  </MenuItem>
                  {stopsData
                    .filter(
                      (s) =>
                        formData.type === "BI_DIRECTIONAL" ||
                        s.id !== formData.drop,
                    )
                    .map((stop) => (
                      <MenuItem key={stop.id} value={stop.id}>
                        {stop.name}
                      </MenuItem>
                    ))}
                </StyledSelect>
              </FormControl>
              {touched.pickUp && errors.pickUp && (
                <ErrorText>{errors.pickUp}</ErrorText>
              )}
            </Grid>

            <Grid item xs={4}>
              <Typography
                variant="body2"
                sx={{
                  my: 1,
                  fontWeight: 400,
                  fontFamily: "Roboto",
                  fontSize: "20px",
                  color: "#414958",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {t("Drop")}{" "}
                {formData.type !== "BI_DIRECTIONAL" && <RequiredAsterisk />}
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={formData.drop}
                  onChange={handleChange("drop")}
                  displayEmpty
                  disabled={formData.type === "BI_DIRECTIONAL"}
                >
                  <MenuItem value="" disabled>
                    {t("Select Drop")}
                  </MenuItem>
                  {stopsData
                    .filter(
                      (s) =>
                        formData.type === "BI_DIRECTIONAL" ||
                        s.id !== formData.pickUp,
                    )
                    .map((stop) => (
                      <MenuItem key={stop.id} value={stop.id}>
                        {stop.name}
                      </MenuItem>
                    ))}
                </StyledSelect>
              </FormControl>
              {touched.drop && errors.drop && (
                <ErrorText>{errors.drop}</ErrorText>
              )}
            </Grid>
          </Grid>

          {/* Stops */}
          <Box mt={3}>
            <SectionTitle>{t("Stops on this Route")}</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2} mb={2}>
                  <FormControl fullWidth>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                      }}
                    >
                      {t("Stops")}
                    </Typography>
                    <StyledSelect
                      multiple
                      value={formData.stops}
                      onChange={handleStopsChange}
                      displayEmpty
                      renderValue={() => t("Select Stops to define path")}
                    >
                      {stopsData
                        .filter(
                          (s) =>
                            s.id !== formData.pickUp && s.id !== formData.drop,
                        )
                        .map((stop) => (
                          <MenuItem key={stop.id} value={stop.id}>
                            <Checkbox
                              checked={formData.stops.indexOf(stop.id) > -1}
                            />
                            <ListItemText primary={stop.name} />
                          </MenuItem>
                        ))}
                    </StyledSelect>
                  </FormControl>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ color: "#647490", fontSize: "14px", mb: 1 }}
                  >
                    {t("No stops to select?")}
                  </Typography>
                  <CreateButton onClick={() => navigate("/school-stops")}>
                    {t("+ Create Stops")}
                  </CreateButton>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#647490", fontSize: "14px" }}>
                  {t("Total Selected Stops")}: {formData.stops.length}
                </Typography>
                <Box
                  sx={{
                    minHeight: "120px",
                    overflowY: "auto",
                    mt: 1,
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "12px",
                    border: "1px solid #B7BFCC",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignContent: "flex-start",
                  }}
                >
                  {formData.stops.map((stopId) => {
                    const stop = stopsData.find((s) => s.id === stopId);
                    return (
                      <Chip
                        key={stopId}
                        label={stop?.name || stopId}
                        onDelete={() => handleRemoveStop(stopId)}
                        sx={{
                          backgroundColor: "#E8EAEE",
                          height: "32px",
                          fontSize: "14px",
                          borderRadius: "4px",
                          padding: "8px 16px",
                          color: "#008C23",
                        }}
                      />
                    );
                  })}
                  {getIntermediateRoutePoints().map((point) => (
                    <Chip
                      key={point.id}
                      label={point.name}
                      onDelete={() => handleRemoveIntermediatePoint(point.id)}
                      sx={{
                        backgroundColor: "#E8EAEE",
                        height: "32px",
                        fontSize: "14px",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        color: "#183059",
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Vehicles */}
          <Box mt={6}>
            <SectionTitle>{t("Vehicles on this Route")}</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2} mb={2}>
                  <FormControl fullWidth>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: "20px",
                        color: "#414958",
                      }}
                    >
                      {t("Assign Vehicles")}
                    </Typography>
                    <StyledSelect
                      multiple
                      value={formData.vehicles}
                      onChange={handleVehiclesChange}
                      displayEmpty
                      renderValue={() => t("Select Vehicles")}
                    >
                      {vehicleData.map((vehicle) => (
                        <MenuItem key={vehicle.id} value={vehicle.id}>
                          <Checkbox
                            checked={formData.vehicles.indexOf(vehicle.id) > -1}
                          />
                          <ListItemText
                            primary={vehicle.name || vehicle.VehicleNumber}
                          />
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ color: "#647490", fontSize: "14px", mb: 1 }}
                  >
                    {t("No vehicles to select?")}
                  </Typography>
                  <CreateButton onClick={() => navigate("/school-vehicles")}>
                    {t("+ Create Vehicles")}
                  </CreateButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: "#647490", fontSize: "14px" }}>
                  {t("Total Assigned Vehicles")}: {formData.vehicles.length}
                </Typography>
                <Box
                  sx={{
                    minHeight: "120px",
                    overflowY: "auto",
                    mt: 1,
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "12px",
                    border: "1px solid #B7BFCC",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignContent: "flex-start",
                  }}
                >
                  {formData.vehicles.map((vehicleId) => {
                    const vehicle = vehicleData.find((v) => v.id === vehicleId);
                    return (
                      <Chip
                        key={vehicleId}
                        label={
                          vehicle?.name || vehicle?.VehicleNumber || vehicleId
                        }
                        onDelete={() => handleRemoveVehicle(vehicleId)}
                        sx={{
                          backgroundColor: "#E8EAEE",
                          height: "32px",
                          borderRadius: "4px",
                          padding: "8px 16px",
                          fontSize: "14px",
                          color: "#1F242E",
                        }}
                      />
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* ── Right: Map ── */}
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              height: "100%",
              minHeight: "600px",
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              border: "1px solid #C8CBD0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={13}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={
                formData.pickUp && formData.drop ? handleMapClick : undefined
              }
              options={getMapOptions({
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: true,
                streetViewControl: false,
                cursor: formData.pickUp && formData.drop ? "crosshair" : "grab",
              })}
            >
              {placeMarkers.map((place) => {
                const config = getPlaceConfig(place.type);
                if (!config?.icon) return null;

                return (
                  <Marker
                    key={place.id}
                    position={place.position}
                    title={place.name}
                    icon={{
                      url: config.icon,
                      scaledSize: new window.google.maps.Size(32, 32),
                    }}
                  />
                );
              })}

              <Autocomplete
                onLoad={onLoadAutocomplete}
                onPlaceChanged={onPlaceChanged}
              >
                <SearchBox>
                  <SearchTextField
                    fullWidth
                    placeholder={t("Search")}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#414958" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </SearchBox>
              </Autocomplete>

              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true,
                    preserveViewport: true,
                    polylineOptions: {
                      strokeColor: formData.color,
                      strokeOpacity: 0.8,
                      strokeWeight: 5,
                    },
                  }}
                />
              )}

              {/* Origin marker */}
              {formData.pickUp &&
                (() => {
                  const stop = stopsData.find((s) => s.id === formData.pickUp);
                  if (!stop) return null;
                  return (
                    <>
                      <Marker
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                        // icon={getOriginStopIcon()}
                        icon={getDestinationStopIcon()}
                        title={`Pickup: ${stop.name}`}
                      />
                      <OverlayView
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#20A8E0",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            position: "absolute",
                            transform: "translate(-50%, -60px)",
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        >
                          {stop.name}
                        </Box>
                      </OverlayView>
                    </>
                  );
                })()}

              {/* Destination marker */}
              {formData.drop &&
                (() => {
                  const stop = stopsData.find((s) => s.id === formData.drop);
                  if (!stop) return null;
                  return (
                    <>
                      <Marker
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                        // icon={getDestinationStopIcon()}
                        icon={getOriginStopIcon()}
                        title={`Drop: ${stop.name}`}
                      />
                      <OverlayView
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#EE3B2B",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            position: "absolute",
                            transform: "translate(-50%, -60px)",
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        >
                          {stop.name}
                        </Box>
                      </OverlayView>
                    </>
                  );
                })()}

              {/* Intermediate (non-stop) points */}
              {getIntermediateRoutePoints().map((point) => (
                <Marker
                  key={point.id}
                  position={{ lat: point.lat, lng: point.lng }}
                  icon={getIntermediatePointIcon()}
                  title={point.name}
                />
              ))}

              {/* Intermediate stop markers */}
              {getIntermediateStops().map((stopId) => {
                const stop = stopsData.find((s) => s.id === stopId);
                if (!stop) return null;
                return (
                  <React.Fragment key={stopId}>
                    <Marker
                      position={{
                        lat: parseFloat(stop.latitude),
                        lng: parseFloat(stop.longitude),
                      }}
                      icon={getIntermediateIcon()}
                      title={stop.name}
                    />
                    <OverlayView
                      position={{
                        lat: parseFloat(stop.latitude),
                        lng: parseFloat(stop.longitude),
                      }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#008C23",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          position: "absolute",
                          transform: "translate(-50%, -25px)",
                          whiteSpace: "nowrap",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                      >
                        {stop.name}
                      </Box>
                    </OverlayView>
                  </React.Fragment>
                );
              })}
            </GoogleMap>
          </Box>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={6}
      >
        <Box>
          {isEdit && onDelete && (
            <>
              <DeleteButton
                onClick={handleDeleteClick}
                startIcon={<DeleteIcon />}
              >
                {t("Delete")}
              </DeleteButton>
            </>
          )}
        </Box>
        <Box display="flex" gap={8}>
          {!isEdit && <CancelButton onClick={onClose}>{t("Cancel")}</CancelButton>}
          <ConfirmButton onClick={handleSubmit}>
            {isEdit ? t("Update") : t("Confirm")}
          </ConfirmButton>
        </Box>
        <Box />
      </Box>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Route!")}
        message={t(
          "Are you sure you want to delete this route? This action cannot be undone.",
        )}
        cancelButtonText={t("Cancel")}
        confirmButtonText={t("Delete")}
      />
      {deleteError && (
        <Typography
          sx={{
            color: "#cc0000",
            fontSize: "13px",
            maxWidth: "400px",
            padding: "8px 12px",
          }}
        >
          {deleteError}
        </Typography>
      )}
    </FormContainer>
  );
}
